import { useEffect, useId, useState, type ChangeEvent, type DragEvent } from "react";
import { CloudUpload, FileText, Film, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  addAttachments,
  ATTACHMENT_ACCEPT,
  ATTACHMENT_HINT,
  ATTACHMENT_KIND_LABELS,
  ATTACHMENT_MAX_FILES,
  formatFileSize,
  getAttachmentKind,
} from "./supportRequest";

/**
 * Mesmo efeito do `sr-only`, sem a classe: o index.css mostra todo `.sr-only` focado (regra dos skip links), e o
 * input de arquivo recebe foco ao clicar na área — com `sr-only`, o input nativo aparecia.
 */
const VISUALLY_HIDDEN_INPUT =
  "absolute -m-px h-px w-px overflow-hidden whitespace-nowrap border-0 p-0 [clip:rect(0,0,0,0)]";

/** Miniatura das imagens (URL local, revogada ao sair); vídeo, PDF e ambientes sem `createObjectURL` ficam com o ícone. */
const AttachmentThumbnail = ({ file }: { file: File }) => {
  const kind = getAttachmentKind(file);
  const isImage = kind === "image";
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isImage || typeof URL.createObjectURL !== "function") return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isImage]);

  if (previewUrl) {
    return <img src={previewUrl} alt="" className="h-11 w-11 shrink-0 rounded-md border object-cover" />;
  }
  const Icon = kind === "video" ? Film : FileText;
  return (
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
      <Icon className="h-5 w-5" aria-hidden="true" />
    </span>
  );
};

interface AttachmentPickerProps {
  files: File[];
  onChange: (files: File[]) => void;
  /** Erro dos anexos: falta de anexo obrigatório ou recusa da API (400 por campo ou 413). */
  error?: string;
  /** Pelo menos um arquivo (troca); a validação fica no formulário, aqui só muda o rótulo. */
  required?: boolean;
  disabled?: boolean;
}

/** Área "Anexar fotos, vídeos ou documentos": clique ou arraste; os arquivos vão no envio da solicitação. */
export const AttachmentPicker = ({ files, onChange, error, required, disabled }: AttachmentPickerProps) => {
  const inputId = useId();
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const [rejections, setRejections] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const isFull = files.length >= ATTACHMENT_MAX_FILES;
  const isInactive = disabled || isFull;

  const add = (incoming: File[]) => {
    if (incoming.length === 0) return;
    const result = addAttachments(files, incoming);
    setRejections(result.rejections);
    if (result.files.length !== files.length) onChange(result.files);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    add(Array.from(event.target.files ?? []));
    // Limpa a seleção pra o mesmo arquivo poder ser escolhido de novo depois de removido.
    event.target.value = "";
  };

  const handleDragOver = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    if (!isInactive) setIsDragging(true);
  };

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (!disabled) add(Array.from(event.dataTransfer.files));
  };

  const handleRemove = (file: File) => {
    setRejections([]);
    onChange(files.filter((current) => current !== file));
  };

  const messages = [...rejections, ...(error ? [error] : [])];

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-[13px] font-bold text-muted-foreground">
          Anexos {required ? "(obrigatório)" : "(opcional)"}
        </span>
        <span className="text-xs text-muted-foreground" aria-live="polite">
          {files.length} de {ATTACHMENT_MAX_FILES} arquivos
        </span>
      </div>

      <label
        htmlFor={inputId}
        onDragOver={handleDragOver}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={cn(
          // `relative` mantém o input escondido dentro da área (o foco não rola o modal); o anel só aparece no teclado.
          "relative flex cursor-pointer flex-col items-center gap-2 rounded-[10px] border-[1.5px] border-dashed px-6 py-6 text-center transition-colors hover:border-primary/60 hover:bg-primary/5 [&:has(:focus-visible)]:ring-2 [&:has(:focus-visible)]:ring-ring",
          isDragging && "border-primary bg-primary/5",
          !!error && "border-destructive/60",
          isInactive && "cursor-not-allowed opacity-60 hover:border-input hover:bg-transparent"
        )}
      >
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CloudUpload className="h-5 w-5" aria-hidden="true" />
        </span>
        <span className="text-sm font-semibold">
          {isFull ? (
            "Limite de arquivos atingido"
          ) : (
            <>
              Arraste os arquivos aqui ou <span className="text-primary underline underline-offset-2">escolha no computador</span>
            </>
          )}
        </span>
        <span id={hintId} className="text-xs text-muted-foreground">
          {ATTACHMENT_HINT}
        </span>
        <input
          id={inputId}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          onChange={handleInputChange}
          disabled={isInactive}
          aria-label="Anexar fotos, vídeos ou documentos"
          aria-required={required || undefined}
          aria-describedby={messages.length > 0 ? `${hintId} ${errorId}` : hintId}
          aria-invalid={!!error}
          className={VISUALLY_HIDDEN_INPUT}
        />
      </label>

      {files.length > 0 && (
        <ul className="grid gap-2 sm:grid-cols-2" aria-label="Arquivos anexados">
          {files.map((file) => (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}`}
              className="flex items-center gap-3 rounded-[10px] border bg-background p-2 pr-1.5"
            >
              <AttachmentThumbnail file={file} />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-[13.5px] font-medium" title={file.name}>
                  {file.name}
                </span>
                <span className="text-xs text-muted-foreground">
                  {ATTACHMENT_KIND_LABELS[getAttachmentKind(file)]} · {formatFileSize(file.size)}
                </span>
              </span>
              <button
                type="button"
                onClick={() => handleRemove(file)}
                disabled={disabled}
                aria-label={`Remover ${file.name}`}
                className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {messages.length > 0 && (
        <div id={errorId} role="alert" className="flex flex-col gap-0.5 text-[13px] font-medium text-destructive">
          {messages.map((message) => (
            <p key={message}>{message}</p>
          ))}
        </div>
      )}
    </div>
  );
};
